"use client";

import * as React from "react";
import { X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { cn } from "@/lib/utils";
import type { MobileBottomToolId, MobileBottomToolItem } from "./types";
import {
  MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
  MOBILE_BOTTOM_TOOL_DOCK_HEIGHT_PX,
} from "./constants";

interface MobileBottomToolSheetProps {
  tool: MobileBottomToolItem | null;
  mobileOnly?: boolean;
  className?: string;
  onClose: () => void;
}

export function MobileBottomToolSheet({
  tool,
  mobileOnly = true,
  className,
  onClose,
}: MobileBottomToolSheetProps) {
  const sheet = tool?.sheet;
  const [heightPx, setHeightPx] = React.useState<number | null>(null);
  const isOpen = Boolean(tool && sheet);

  useBodyScrollLock(isOpen);

  React.useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const { body } = document;

    root.toggleAttribute("data-mobile-bottom-sheet-open", true);
    body.toggleAttribute("data-mobile-bottom-sheet-open", true);

    return () => {
      root.removeAttribute("data-mobile-bottom-sheet-open");
      body.removeAttribute("data-mobile-bottom-sheet-open");
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!sheet) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, sheet]);

  const getClampedHeight = React.useCallback(
    (height: number) => {
      if (typeof window === "undefined") return height;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const minHeight = viewportHeight * (sheet?.minHeightRatio ?? 0.3);
      const maxByRatio = viewportHeight * (sheet?.maxHeightRatio ?? 0.85);
      const maxByDock = viewportHeight - MOBILE_BOTTOM_TOOL_DOCK_HEIGHT_PX - 16;
      const maxHeight = Math.max(minHeight, Math.min(maxByRatio, maxByDock));
      return Math.min(Math.max(height, minHeight), maxHeight);
    },
    [sheet?.maxHeightRatio, sheet?.minHeightRatio],
  );

  const getDefaultHeight = React.useCallback(() => {
    if (typeof window === "undefined") return null;
    return getClampedHeight(
      window.innerHeight * (sheet?.defaultHeightRatio ?? 0.3),
    );
  }, [getClampedHeight, sheet?.defaultHeightRatio]);

  const handleResizePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!sheet?.resizable) return;
      event.preventDefault();

      const startY = event.clientY;
      const startHeight = heightPx ?? getDefaultHeight();
      if (!startHeight) return;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        setHeightPx(getClampedHeight(startHeight + startY - moveEvent.clientY));
      };
      const stopResize = () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", stopResize);
        window.removeEventListener("pointercancel", stopResize);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", stopResize);
      window.addEventListener("pointercancel", stopResize);
    },
    [getClampedHeight, getDefaultHeight, heightPx, sheet?.resizable],
  );

  const handleResizeKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!sheet?.resizable) return;
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

      event.preventDefault();
      const delta = event.shiftKey ? 80 : 24;
      const direction = event.key === "ArrowUp" ? 1 : -1;
      setHeightPx((current) =>
        getClampedHeight((current ?? getDefaultHeight() ?? 0) + delta * direction),
      );
    },
    [getClampedHeight, getDefaultHeight, sheet?.resizable],
  );

  const closeOnContentInteraction = sheet?.closeOnContentInteraction;
  const handleContentClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!closeOnContentInteraction) return;
      const selector =
        typeof closeOnContentInteraction === "string"
          ? closeOnContentInteraction
          : "a, button";
      // 빈 영역 탭으로는 닫지 않고 실제 조작에만 반응한다. target 은 아이콘 SVG 일 수 있어 Element.
      if ((event.target as Element).closest(selector)) onClose();
    },
    [closeOnContentInteraction, onClose],
  );

  React.useEffect(() => {
    if (!sheet) return;

    const reclampHeight = () => {
      setHeightPx((current) =>
        current === null ? null : getClampedHeight(current),
      );
    };

    window.addEventListener("resize", reclampHeight);
    window.visualViewport?.addEventListener("resize", reclampHeight);
    return () => {
      window.removeEventListener("resize", reclampHeight);
      window.visualViewport?.removeEventListener("resize", reclampHeight);
    };
  }, [getClampedHeight, sheet]);

  if (!tool || !sheet) return null;

  const isResizable = Boolean(sheet.resizable);
  // dock 에 붙는 모양(full-width·위만 둥글게·바닥 gap 0)은 resizable 과 분리. resizable 시트는
  // 드래그 핸들이 있어 항상 붙고, 비-resizable 도 attached 면 붙되 높이는 콘텐츠만큼만 쓴다.
  const isAttached = isResizable || Boolean(sheet.attached);
  const titleId = `mobile-bottom-tool-sheet-title-${tool.id}`;
  const descriptionId = sheet.description
    ? `mobile-bottom-tool-sheet-description-${tool.id}`
    : undefined;
  const defaultHeight = `${(sheet.defaultHeightRatio ?? 0.3) * 100}dvh`;

  return (
    <section
      id={`mobile-bottom-tool-sheet-${tool.id as MobileBottomToolId}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className={cn(
        "not-prose fixed z-40 mx-auto max-w-md touch-pan-y overflow-hidden overscroll-contain border-border bg-card text-card-foreground shadow-2xl",
        isAttached
          ? "inset-x-0 rounded-t-lg border-x border-t"
          : "inset-x-2 rounded-lg border",
        // 클래식 레이아웃 전 구간(<1024px, 셸 게이트와 일치)에서 노출
        mobileOnly && "lg:hidden",
        className,
      )}
      style={{
        "--mobile-bottom-tool-dock-height": MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
        bottom:
          isAttached
            ? "calc(var(--mobile-bottom-tool-dock-height, 72px) + env(safe-area-inset-bottom))"
            : "calc(var(--mobile-bottom-tool-dock-height, 72px) + env(safe-area-inset-bottom) + 8px)",
        height: isResizable
          ? heightPx
            ? `${heightPx}px`
            : defaultHeight
          : undefined,
        maxHeight:
          "calc(100dvh - var(--mobile-bottom-tool-dock-height, 72px) - env(safe-area-inset-bottom) - 16px)",
      } as React.CSSProperties}
    >
      <div className="flex h-full max-h-[inherit] min-h-0 flex-col">
        {isResizable && (
          <button
            type="button"
            onPointerDown={handleResizePointerDown}
            onKeyDown={handleResizeKeyDown}
            aria-label="패널 높이 조절"
            title="패널 높이 조절"
            className="flex h-11 shrink-0 select-none cursor-ns-resize touch-none items-center justify-center rounded-t-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset"
          >
            <span className="h-1 w-10 rounded-full bg-muted-foreground/35" />
          </button>
        )}
        <header className="not-prose flex items-start gap-2 border-b border-border px-3 py-2">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="m-0 truncate text-sm font-semibold leading-5"
            >
              {sheet.title}
            </h2>
            {sheet.description && (
              <p
                id={descriptionId}
                className="m-0 mt-0.5 text-xs leading-4 text-muted-foreground"
              >
                {sheet.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="패널 닫기"
            className="inline-flex h-8 w-8 shrink-0 touch-manipulation items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>
        <div
          className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-3 py-3 [-webkit-overflow-scrolling:touch]"
          onClick={handleContentClick}
        >
          {sheet.content}
        </div>
      </div>
    </section>
  );
}
